<template>
  <div class="content-main">
    <div class="content" style="width: 100%">
      <div class="header">Movement History</div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3 px-4 py-3 items-end">
        <div>
          <label class="block text-xs font-medium mb-1">NKR Code</label>
          <input
            v-model="filters.nkr_code"
            class="border rounded px-3 py-2 text-sm"
            placeholder="NKR Code"
            @input="debouncedLoad"
          />
        </div>
        <div>
          <label class="block text-xs font-medium mb-1">Type</label>
          <select
            v-model="filters.movement_type"
            class="border rounded px-3 py-2 text-sm"
            @change="loadMovements"
          >
            <option value="">All</option>
            <option value="adjustment">Adjustment</option>
            <option value="dispatch">Dispatch</option>
            <option value="reception">Reception</option>
            <option value="transfer_in">Transfer In</option>
            <option value="transfer_out">Transfer Out</option>
            <option value="initial_load">Initial Load</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium mb-1">Warehouse</label>
          <select
            v-model="filters.warehouse_id"
            class="border rounded px-3 py-2 text-sm"
            @change="loadMovements"
          >
            <option value="">All</option>
            <option v-for="dep in depositos" :key="dep.id" :value="dep.id">{{
              dep.nombre || dep.codDeposito
            }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium mb-1">From</label>
          <input
            v-model="filters.date_from"
            type="date"
            class="border rounded px-3 py-2 text-sm"
            @change="loadMovements"
          />
        </div>
        <div>
          <label class="block text-xs font-medium mb-1">To</label>
          <input
            v-model="filters.date_to"
            type="date"
            class="border rounded px-3 py-2 text-sm"
            @change="loadMovements"
          />
        </div>
      </div>

      <!-- Table -->
      <div class="table">
        <table class="default-table" v-if="!loading">
          <thead>
            <tr class="table100-head">
              <th>Date</th>
              <th>NKR Code</th>
              <th>Line</th>
              <th>Warehouse</th>
              <th>Type</th>
              <th>Qty</th>
              <th>Balance</th>
              <th>User</th>
              <th>Classification</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in movements" :key="m.id">
              <td class="text-xs">{{ formatDate(m.createdAt) }}</td>
              <td>{{ m.nkrCode }}</td>
              <td>{{ m.line }}</td>
              <td>{{ m.warehouseCode }}</td>
              <td>
                <span
                  class="px-2 py-1 text-xs rounded"
                  :class="typeClass(m.movementType)"
                >
                  {{ m.movementType }}
                </span>
              </td>
              <td>{{ m.quantity }}</td>
              <td>{{ m.balanceAfter }}</td>
              <td>{{ m.userName || m.userUuid || "—" }}</td>
              <td>{{ m.classification || "—" }}</td>
              <td class="text-xs" style="max-width: 200px">
                {{ m.notes || "—" }}
              </td>
            </tr>
            <tr v-if="movements.length === 0">
              <td colspan="10" class="text-center text-gray-500 py-4">
                No movements found
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="p-8 text-center">Loading...</div>
      </div>

      <!-- Pagination -->
      <div class="footer">
        <div class="totals">
          showing {{ perPage * (page - 1) + 1 }} to
          {{ Math.min(perPage * page, totalItems) }} from
          {{ totalItems }} results
        </div>
        <div class="pages">
          <select
            v-model="perPage"
            @change="
              page = 1;
              loadMovements();
            "
          >
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
          Per Page
        </div>
        <div class="pages">
          <a
            class="page-link"
            :class="{ disabled: page <= 1 }"
            @click="previous"
            >Previous</a
          >
          <span>Page {{ page }}</span>
          <a
            class="page-link"
            :class="{ disabled: page >= totalPages }"
            @click="next"
            >Next</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMovementHistory } from "@/api/Products.js";
import { getDepositos } from "@/api/Infoauto.js";

export default {
  name: "MovementHistoryPage",
  data() {
    return {
      movements: [],
      depositos: [],
      loading: false,
      page: 1,
      perPage: 50,
      totalItems: 0,
      filters: {
        nkr_code: "",
        movement_type: "",
        warehouse_id: "",
        date_from: "",
        date_to: ""
      },
      debounceTimer: null
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.perPage) || 1;
    }
  },
  async mounted() {
    try {
      this.depositos = await getDepositos();
    } catch {
      this.depositos = [];
    }
  },
  methods: {
    async loadMovements() {
      if (!this.filters.nkr_code) return;
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filters.nkr_code) params.nkr_code = this.filters.nkr_code;
        if (this.filters.movement_type)
          params.movement_type = this.filters.movement_type;
        if (this.filters.warehouse_id)
          params.warehouse_id = this.filters.warehouse_id;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        const data = await getMovementHistory(params);
        this.movements = data.data || data;
        this.totalItems = data.total || this.movements.length;
      } finally {
        this.loading = false;
      }
    },
    debouncedLoad() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.page = 1;
        this.loadMovements();
      }, 300);
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleString() : "—";
    },
    typeClass(t) {
      const map = {
        adjustment: "bg-orange-100 text-orange-800",
        dispatch: "bg-red-100 text-red-800",
        reception: "bg-green-100 text-green-800",
        transfer_in: "bg-blue-100 text-blue-800",
        transfer_out: "bg-purple-100 text-purple-800",
        initial_load: "bg-gray-100 text-gray-800"
      };
      return map[t] || "bg-gray-100 text-gray-800";
    },
    previous() {
      if (this.page > 1) {
        this.page--;
        this.loadMovements();
      }
    },
    next() {
      if (this.page < this.totalPages) {
        this.page++;
        this.loadMovements();
      }
    }
  }
};
</script>
